import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import "./UserManagementPopup.css";
import "./UserManagementCreatePopup.css";
import axios from "axios";
import InputContainer from "../../../../components/Util/InputContainer";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../../utils/portConfig";

const UserCreatePopup = ({ isOpen, onClose, onSave, userBasicData }) => {
  const initialUserData = {
    userId: "",
    userName: "",
    comment: "",
    password: "",
    confirmPassword: "",
    hospital: 0,
    auth: 3, // 기본값을 3으로 설정 (doctor)
  };

  const [userData, setUserData] = useState({ ...initialUserData });
  const [idValidationMessage, setIdValidationMessage] = useState("");
  const [passwordValidationMessage, setPasswordValidationMessage] = useState("");
  const [hospitalOptions, setHospitalOptions] = useState([{ hospitalLabel: "소속병원 없음", id: 0 }]); // 병원 옵션을 저장할 상태 변수 추가
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const userAuth = localStorage.getItem("auth");

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/users`;
  const token = localStorage.getItem("token");
  const id = localStorage.getItem("id");

  // 입력값 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "birthDate") {
      // 사용자 입력 시 숫자와 하이픈만 허용
      const filteredValue = value.replace(/[^\d-]/g, "");
      // 입력된 값에서 숫자만 추출
      const numericValue = filteredValue.replace(/\D/g, "");

      // 최대 8자리까지만 허용 (YYYYMMDD)
      if (numericValue.length <= 8) {
        let formattedValue = numericValue;

        // 자동 포맷팅: YYYY-MM-DD
        if (numericValue.length > 4) {
          formattedValue = `${numericValue.substring(0, 4)}-${numericValue.substring(4, Math.min(6, numericValue.length))}`;
        }

        if (numericValue.length > 6) {
          formattedValue = `${numericValue.substring(0, 4)}-${numericValue.substring(4, 6)}-${numericValue.substring(6, 8)}`;
        }

        setUserData({
          ...userData,
          [name]: formattedValue,
        });
      }
    } else if (name === "telNumber") {
      // 휴대폰 번호 형식(000-0000-0000)만 허용
      const rawValue = value.replace(/[^0-9]/g, "");
      let formattedValue = "";

      // 최대 11자리까지만 입력 허용
      if (rawValue.length <= 11) {
        // 휴대폰 번호 형식(010, 011, 016, 017, 018, 019로 시작)
        if (rawValue.startsWith("010") || rawValue.startsWith("011") || rawValue.startsWith("016") || rawValue.startsWith("017") || rawValue.startsWith("018") || rawValue.startsWith("019")) {
          // 자동 포맷팅
          if (rawValue.length <= 3) {
            formattedValue = rawValue;
          } else if (rawValue.length <= 7) {
            formattedValue = `${rawValue.substring(0, 3)}-${rawValue.substring(3)}`;
          } else {
            formattedValue = `${rawValue.substring(0, 3)}-${rawValue.substring(3, 7)}-${rawValue.substring(7)}`;
          }

          setUserData({
            ...userData,
            [name]: formattedValue,
          });
        } else if (rawValue.length <= 3) {
          // 아직 3자리 미만이면 일단 입력 허용 (010 등의 시작 번호를 입력 중일 수 있음)
          setUserData({
            ...userData,
            [name]: rawValue,
          });
        }
      }
    } else if (name === "userName" || name === "userId") {
      // 사용자명에서 공백 제거
      const noSpaceValue = value.replace(/\s/g, "");
      setUserData({
        ...userData,
        [name]: noSpaceValue,
      });
    } else if (name === "password" || name === "confirmPassword") {
      // 비밀번호 필드에서 공백 제거
      const noSpaceValue = value.replace(/\s/g, "");
      setUserData({
        ...userData,
        [name]: noSpaceValue,
      });
    } else {
      // 다른 필드는 기존 방식으로 처리
      setUserData({
        ...userData,
        [name]: value,
      });
    }
  };

  // 모달이 열렸을 때 초기화
  useEffect(() => {
    if (!isOpen) {
      setUserData({ ...initialUserData });
      setPasswordValidationMessage("");
      setIdValidationMessage("");
      setSelectedFile(null);
      setImagePreview(null);
      setHospitalOptions([{ hospitalLabel: "해당사항 없음", id: 0 }]);
    }
  }, [isOpen]);

  // 사용자 ID 변경 시 ID 중복 검사 메시지 초기화
  useEffect(() => {
    setIdValidationMessage("");
  }, [userData.userId]);

  // 비밀번호 일치 여부 확인 핸들러
  const handleBlur = () => {
    if (userData.confirmPassword) {
      if (userData.password !== userData.confirmPassword) {
        setPasswordValidationMessage("비밀번호가 일치하지 않습니다.");
      } else {
        setPasswordValidationMessage("비밀번호가 일치합니다.");
      }
    } else {
      setPasswordValidationMessage("");
    }
  };

  // 병원 옵션 가져오는 함수
  const fetchHospitals = async () => {
    try {
      const response = await axios.get(`${getServerBaseUrl()}/api/v1/hospital/dropdownList/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setHospitalOptions((prevOptions) => [...prevOptions, ...response.data]);
      } else {
        const responseData = response.data;
        if (responseData.status === "error") {
          Swal.fire("오류", responseData.message, "error");
        } else {
          Swal.fire("오류", "병원 목록을 가져오는데 실패했습니다.", "error");
        }
      }
    } catch (error) {
      console.error(error);
      Swal.fire("오류", "서버와 통신 중 에러가 발생했습니다.", "error");
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchHospitals(); // 병원 데이터를 가져옵니다.
    }
  }, [isOpen]);

  // 사용자 ID 중복 확인 핸들러
  const handleCheckDuplicate = async () => {
    const regex = /^[a-zA-Z0-9]+$/;
    if (!regex.test(userData.userId)) {
      Swal.fire("오류", "영문 대소문자와 숫자만 입력 가능합니다.", "warning");
      return;
    }

    if (!userData.userId.trim()) {
      Swal.fire("알림", "사용자 ID를 입력해주세요.", "warning");
      return;
    }

    if (userData.userId.length < 4 || userData.userId.length > 20) {
      Swal.fire("알림", "사용자 ID는 4자 이상 20자 이하로 입력해주세요.", "warning");
      return;
    }

    try {
      const response = await axios.get(`${API_SERVER_URL}/${userData.userId}/check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        if (response.data) {
          setIdValidationMessage("중복된 ID입니다.");
        } else {
          setIdValidationMessage("사용 가능한 ID입니다.");
        }
      } else {
        Swal.fire("오류", "중복확인 요청에 실패했습니다. 다시 시도해주세요.", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 사용자 저장 버튼 클릭 핸들러
  const handleSaveClick = () => {
    if (idValidationMessage !== "사용 가능한 ID입니다.") {
      Swal.fire("알림", "아이디 중복확인을 해주세요.", "warning");
      return;
    }

    if (userData.password !== userData.confirmPassword) {
      setPasswordValidationMessage("비밀번호가 일치하지 않습니다.");
      return;
    }

    if (passwordValidationMessage !== "비밀번호가 일치합니다.") {
      Swal.fire("알림", "비밀번호를 확인해주세요.", "warning");
      return;
    }
    if (userAuth === "Admin") {
      userData.hospital = userBasicData.hospitalId;
    }
    if (userData.hospital === 0 && (Number(userData.auth) === 2 || Number(userData.auth) === 3)) {
      Swal.fire("알림", "소속 병원을 선택해 주세요.", "warning");
      return;
    }

    const userJson = {
      userId: userData.userId.trim(),
      userName: userData.userName.trim(),
      comment: userData.comment.trim(),
      password: userData.password,
      hospitalId: userData.hospital,
      roleId: userData.auth,
      enabled: true,
    };

    if (selectedFile) {
      userJson.profileImg = selectedFile;
    }

    onSave(userJson);
  };

  // 사용자 프로필 이미지 삭제
  const removeUserProfile = () => {
    setSelectedFile(null);
    setImagePreview(null);
  };

  // 사용자 프로필 이미지 업로드
  const userProfileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);

    // 이미지 미리보기 업데이트
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="User Popup"
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
        },
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          padding: "10px 20px 10px 20px",
          overflow: "hidden",
          border: "1px solid #ccc",
          background: "white",
          borderRadius: "4px",
          width: "400px",
          height: "660px",
        },
      }}
    >
      <div className="user-modal-content">
        <div className="user-modal-header">
          <h2>사용자 생성</h2>
        </div>
        <div className="input-container" style={{ marginBottom: "10px" }}>
          <div className="input-row">
            <label className="move-center">사용자 프로필</label>
            <div className="input-field move-img-container-centner">
              <div className="selected-image-preview">
                {!selectedFile && !imagePreview && (
                  <span className="img-upload-text">
                    이미지를 업로드하세요.
                    <br />
                    권장: 650 x 650
                  </span>
                )}
                {(selectedFile || imagePreview) && (
                  <>
                    <img src={imagePreview || URL.createObjectURL(selectedFile)} alt="Selected Preview" />
                    <span className="remove-image-button" onClick={removeUserProfile}>
                      X
                    </span>
                  </>
                )}
              </div>
              <div className="file-upload-style">
                <input
                  id="fileInput"
                  type="file"
                  accept="images/*"
                  onChange={userProfileChange}
                  style={{ display: "none" }} // 실제 파일 업로드를 숨기기 위해 display 속성을 사용합니다.
                />
                <label htmlFor="fileInput" className="custom-file-upload">
                  <span className="upload-img-icon">+</span> 이미지 업로드
                </label>
              </div>
            </div>
          </div>
        </div>
        {/*<div style={{display:'flex', width: '100%', padding: '3px', marginBottom: '3px'}}>
          <label style={{fontSize: '10px', marginLeft: '4px', color: 'red'}}>&#8251; 권장: 정사각형의 이미지를 업로드해주세요. &#8251;</label>
        </div>*/}
        <InputContainer
          autoComplete="off"
          label="사용자 ID"
          name="userId"
          value={userData.userId}
          onChange={handleChange}
          onBlur={handleBlur}
          validationMessage={idValidationMessage}
          validationColor={idValidationMessage === "중복된 ID입니다." ? "red" : "green"}
          buttonLabel="중복 확인"
          onButtonClick={handleCheckDuplicate}
        />
        <div className="user-tie-container">
          <InputContainer autoComplete="off" label="사용자명" name="userName" value={userData.userName} onChange={handleChange} />
          {userAuth === "Superadmin" ? (
            <InputContainer
              label="권한"
              id="createAuth"
              name="auth"
              value={userData.roleName}
              onChange={handleChange}
              type="select"
              options={[
                { label: "시스템관리자", value: "Superadmin" },
                { label: "병원관리자", value: "Admin" },
                { label: "의사", value: "Doctor" },
                { label: "간호사", value: "Nurce" },
              ]}
            />
          ) : (
            <div className="input-container">
              <div className="input-row">
                <label htmlFor="auth">권한</label>
                <select name="auth" value={userData.auth} onChange={handleChange} className="custom-select" disabled={true}>
                  <option value="3">의사</option>
                </select>
              </div>
            </div>
          )}
        </div>
        <div className="user-tie-container">
          <InputContainer autoComplete="off" label="비밀번호" name="password" value={userData.password} onChange={handleChange} validationMessage={<span>&nbsp;</span>} passwordMask={true} />
          <InputContainer
            autoComplete="off"
            label="비밀번호 확인"
            name="confirmPassword"
            value={userData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            validationMessage={passwordValidationMessage === "" ? <span>&nbsp;</span> : passwordValidationMessage}
            validationColor={passwordValidationMessage === "비밀번호가 일치하지 않습니다." ? "red" : "green"}
            passwordMask={true}
          />
        </div>
        {userAuth === "Superadmin" ? (
          <InputContainer
            label="병원"
            name="hospital"
            value={userData.hospital}
            onChange={handleChange}
            type="select"
            options={hospitalOptions.map((hospital) => ({
              label: hospital.hospitalLabel, // 병원 이름 필드로 변경
              value: hospital.id,
            }))}
          />
        ) : (
          <div className="input-container">
            <div className="input-row">
              <label htmlFor="hospital">병원</label>
              <select name="hospital" value={userData.hospital} onChange={handleChange} className="custom-select" disabled={true}>
                {userBasicData && <option value={userBasicData.hospitalId}>{userBasicData.hospital}</option>}
              </select>
            </div>
          </div>
        )}
        <div className="input-container">
          <div className="input-row">
            <label>사용자 소개</label>
            <div className="input-field user-introduce-style">
              <textarea name="comment" value={userData.comment} placeholder="소개를 입력하세요." onChange={handleChange} maxLength={500} />
            </div>
          </div>
        </div>
        <div className="user-button-container">
          <button className="dashboard-content-button popup-button save-button" onClick={handleSaveClick}>
            저장
          </button>
          <button className="dashboard-content-button red-button popup-button cancel-button" onClick={onClose}>
            취소
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UserCreatePopup;
